/**
 * Playsout API 服务
 * 处理与 Playsout 后端的所有 API 交互
 */

const BASE_URL = 'https://api.playsout.com';
const LOGIN_MESSAGE = 'Welcome to the PlaysOut Airdrop! Sign to verify your identity and start your reward journey.';
const PLAYSOUT_PLATFORM = 'baseminiapp';
const PLAYSOUT_LOGIN_KEY = 'baseLoginRequest';

// localStorage keys
const STORAGE_KEYS = {
  TOKEN: 'playsout_token',
  INVITE_CODE: 'playsout_invite_code',
  WALLET_ADDRESS: 'playsout_wallet_address',
  TOKEN_EXP: 'playsout_token_exp',
};

interface LoginResponse {
  code: number;
  msg: string;
  data: {
    userToken: string;
    botName?: string;
    id: number;
    exp: number;
    platform: string;
  };
  times: number;
}

interface InviteCodeResponse {
  code: number;
  msg: string;
  times: number;
}

export class PlaysoutAPI {
  private token: string | null = null;
  private walletAddress: string | null = null;
  private inviteCode: string | null = null;
  private tokenExp: number | null = null;
  private loginPromise: Promise<string> | null = null;
  private loginAddress: string | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      this.walletAddress = localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS);
      this.inviteCode = localStorage.getItem(STORAGE_KEYS.INVITE_CODE);
      const storedExp = localStorage.getItem(STORAGE_KEYS.TOKEN_EXP);
      this.tokenExp = storedExp ? Number(storedExp) : null;
      if (this.tokenExp !== null && !Number.isFinite(this.tokenExp)) {
        this.tokenExp = null;
      }

      if (this.isTokenExpired()) {
        this.clearSession();
      }
    }
  }

  isLoggedIn(): boolean {
    return !!this.ensureToken() && !!this.walletAddress;
  }

  getToken(): string | null {
    return this.token;
  }

  getInviteCode(): string | null {
    return this.inviteCode;
  }

  getTokenExp(): number | null {
    return this.tokenExp;
  }

  private normalizeTokenExp(exp?: number): number | null {
    if (!exp || !Number.isFinite(exp)) {
      return null;
    }
    return exp > 1_000_000_000_000 ? exp : exp * 1000;
  }

  isTokenExpired(): boolean {
    if (!this.token) {
      return false;
    }

    // 某些历史 token 可能没有 exp，兼容为“未知有效期”
    if (!this.tokenExp) {
      return false;
    }

    const EXPIRY_LEEWAY_MS = 30 * 1000;
    return Date.now() >= this.tokenExp - EXPIRY_LEEWAY_MS;
  }

  private isAuthErrorMessage(message?: string): boolean {
    if (!message) {
      return false;
    }
    const lower = message.toLowerCase();
    return lower.includes('token')
      && (lower.includes('invalid') || lower.includes('expire') || lower.includes('unauth'));
  }

  private async shouldInvalidateSession(response: Response): Promise<boolean> {
    if (response.status === 401 || response.status === 403) {
      return true;
    }

    try {
      const body = await response.clone().json() as { code?: number; msg?: string; message?: string };
      if (body.code === 401 || body.code === 403) {
        return true;
      }
      return this.isAuthErrorMessage(body.msg || body.message);
    } catch {
      return false;
    }
  }

  // 在跨页面导航场景下，确保内存中的 token 与 localStorage 同步
  ensureToken(): string | null {
    if (!this.token && typeof window !== 'undefined') {
      const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (storedToken) {
        this.token = storedToken;
        this.walletAddress = localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS);
        this.inviteCode = localStorage.getItem(STORAGE_KEYS.INVITE_CODE);
        const storedExp = localStorage.getItem(STORAGE_KEYS.TOKEN_EXP);
        this.tokenExp = storedExp ? Number(storedExp) : null;
        if (this.tokenExp !== null && !Number.isFinite(this.tokenExp)) {
          this.tokenExp = null;
        }
        console.log('[PlaysoutAPI] Token restored from localStorage');
      } else {
        console.warn('[PlaysoutAPI] No token found in localStorage');
      }
    }

    if (!this.token) {
      console.warn('[PlaysoutAPI] ensureToken result: null token');
      return null;
    }

    if (this.isTokenExpired()) {
      console.warn('[PlaysoutAPI] Token expired, clearing session');
      this.clearSession();
      return null;
    }

    return this.token;
  }

  checkWalletChange(currentAddress: string): boolean {
    const normalizedAddress = currentAddress.toLowerCase();
    if (this.walletAddress && this.walletAddress !== normalizedAddress) {
      this.clearSession();
      return true;
    }
    return false;
  }

  clearSession(): void {
    this.token = null;
    this.walletAddress = null;
    this.inviteCode = null;
    this.tokenExp = null;
    this.loginPromise = null;
    this.loginAddress = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.WALLET_ADDRESS);
      localStorage.removeItem(STORAGE_KEYS.INVITE_CODE);
      localStorage.removeItem(STORAGE_KEYS.TOKEN_EXP);
    }
  }

  private saveSession(token: string, address: string, exp: number | null): void {
    this.token = token;
    this.walletAddress = address;
    this.tokenExp = exp;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.WALLET_ADDRESS, address);
      if (exp) {
        localStorage.setItem(STORAGE_KEYS.TOKEN_EXP, String(exp));
      } else {
        localStorage.removeItem(STORAGE_KEYS.TOKEN_EXP);
      }
    }
  }

  private saveInviteCode(code: string): void {
    this.inviteCode = code;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.INVITE_CODE, code);
    }
  }

  async login(
    address: string,
    signMessage: (message: string) => Promise<string>,
    inviteCode?: string
  ): Promise<string> {
    const normalizedAddress = address.toLowerCase();

    if (this.ensureToken() && this.walletAddress === normalizedAddress) {
      console.log('[PlaysoutAPI] Already logged in, returning cached token');
      return this.token as string;
    }

    this.checkWalletChange(normalizedAddress);

    if (this.loginPromise && this.loginAddress === normalizedAddress) {
      return this.loginPromise;
    }

    this.loginAddress = normalizedAddress;

    this.loginPromise = (async () => {
      try {
        const signature = await signMessage(LOGIN_MESSAGE);

        const response = await fetch(`${BASE_URL}/user/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            [PLAYSOUT_LOGIN_KEY]: {
              message: LOGIN_MESSAGE,
              address: normalizedAddress,
              signature,
            },
            inviteCode: inviteCode || '',
          }),
        });

        if (!response.ok) {
          throw new Error(`Login failed: HTTP ${response.status}`);
        }

        const data: LoginResponse = await response.json();
        if (data.code !== 10000) {
          throw new Error(data.msg || 'Login failed');
        }

        const userToken = data.data.userToken;
        const tokenExp = this.normalizeTokenExp(data.data.exp);
        this.saveSession(userToken, normalizedAddress, tokenExp);
        console.log('[PlaysoutAPI] Login successful');
        return userToken;
      } catch (error) {
        console.error('[PlaysoutAPI] Login error:', error);
        throw error;
      } finally {
        this.loginPromise = null;
        this.loginAddress = null;
      }
    })();

    try {
      return await this.loginPromise;
    } finally {
      this.loginPromise = null;
      this.loginAddress = null;
    }
  }

  async sendHeartbeat(gameId: string): Promise<boolean> {
    const token = this.ensureToken();
    if (!token) {
      console.warn('[PlaysoutAPI][Heartbeat] Cannot send heartbeat: not logged in', {
        gameId,
        hasWalletAddress: !!this.walletAddress,
        tokenExp: this.tokenExp,
      });
      return false;
    }

    try {
      const heartbeatUrl = `${BASE_URL}/game/heart?gameId=${encodeURIComponent(gameId)}&platform=${encodeURIComponent(PLAYSOUT_PLATFORM)}`;
      console.log('[PlaysoutAPI][Heartbeat] Sending request', {
        gameId,
        platform: PLAYSOUT_PLATFORM,
        url: heartbeatUrl,
        hasToken: !!token,
        tokenPreview: `${token.slice(0, 8)}...`,
      });

      const response = await fetch(
        heartbeatUrl,
        {
          method: 'GET',
          headers: {
            token,
          },
        }
      );

      let responsePreview = '';
      try {
        responsePreview = (await response.clone().text()).slice(0, 200);
      } catch {
        responsePreview = '';
      }

      console.log('[PlaysoutAPI][Heartbeat] Response received', {
        gameId,
        status: response.status,
        ok: response.ok,
        preview: responsePreview,
      });

      if (await this.shouldInvalidateSession(response)) {
        console.warn('[PlaysoutAPI] Heartbeat auth invalid, clearing session');
        this.clearSession();
        return false;
      }

      const success = response.ok;
      if (success) {
        console.log('[PlaysoutAPI] Heartbeat sent successfully for game:', gameId);
      } else {
        console.warn('[PlaysoutAPI] Heartbeat failed with status:', response.status);
      }

      return success;
    } catch (error) {
      console.error('[PlaysoutAPI] Heartbeat error:', error);
      return false;
    }
  }

  async fetchInviteCode(): Promise<string | null> {
    const token = this.ensureToken();
    if (!token) {
      console.warn('[PlaysoutAPI] Cannot fetch invite code: not logged in');
      return null;
    }

    if (this.inviteCode) {
      console.log('[PlaysoutAPI] Returning cached invite code');
      return this.inviteCode;
    }

    try {
      const response = await fetch(`${BASE_URL}/game/inviteCode`, {
        method: 'GET',
        headers: {
          token,
        },
      });

      if (await this.shouldInvalidateSession(response)) {
        console.warn('[PlaysoutAPI] Invite code auth invalid, clearing session');
        this.clearSession();
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch invite code: HTTP ${response.status}`);
      }

      const data: InviteCodeResponse = await response.json();
      if (data.code !== 10000) {
        if (this.isAuthErrorMessage(data.msg)) {
          this.clearSession();
        }
        throw new Error('Failed to fetch invite code');
      }

      const code = data.msg;
      this.saveInviteCode(code);
      console.log('[PlaysoutAPI] Invite code fetched successfully');
      return code;
    } catch (error) {
      console.error('[PlaysoutAPI] Fetch invite code error:', error);
      return null;
    }
  }

  async reportBuy(gameId: string, hash: string): Promise<boolean> {
    const token = this.ensureToken();
    if (!token) {
      console.warn('[PlaysoutAPI] Cannot report buy: not logged in');
      return false;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/game/buy?gameId=${encodeURIComponent(gameId)}&hash=${encodeURIComponent(hash)}&platform=${encodeURIComponent(PLAYSOUT_PLATFORM)}`,
        {
          method: 'GET',
          headers: {
            token,
          },
        }
      );

      const success = response.ok;
      if (success) {
        console.log('[PlaysoutAPI] Buy reported successfully:', { gameId, hash });
      } else {
        console.warn('[PlaysoutAPI] Report buy failed with status:', response.status);
      }

      return success;
    } catch (error) {
      console.error('[PlaysoutAPI] Report buy error:', error);
      return false;
    }
  }
}

let playsoutApiInstance: PlaysoutAPI | null = null;

export function getPlaysoutAPI(): PlaysoutAPI {
  if (!playsoutApiInstance) {
    playsoutApiInstance = new PlaysoutAPI();
  }
  return playsoutApiInstance;
}

export function resetPlaysoutAPI(): void {
  if (playsoutApiInstance) {
    playsoutApiInstance.clearSession();
  }
  playsoutApiInstance = null;
}
